import { AuthMethod } from '@infotech-soe/merps/types/session';
import { SignatureType } from '@infotech-soe/metaschema';
import { DSAgents } from '@infotech-soe/mia-ds-service/types';
import { ConnectedProps, legacy_connect as connect } from 'react-redux';

import { AvailableApplication } from '../../../services/auth/types';
import { SignActionArgs } from '../../../services/commands/types';
import { AppState } from '../../../store/reducers/root';
import { getConnectionId } from '../../../store/selectors/connection';
import {
  getAllowedDSMediaTypes,
  getAvailableApplications,
  getAvailableDSKeyTypes,
  getForbiddenDSMediaTypes,
  getSignAlgo,
  getUserApplications,
} from '../../../store/selectors/session';
import { isDarkTheme } from '../../../store/selectors/theme';
import session from '../../../store/slices/session';
import LoginForm from '../../components/session/LoginForm';
import WindowsADAuthContainer from '../../components/session/WindowsADAuthContainer';
import DSAgentForm from '../../components/sign/DSAgentForm';

type LoginAuthFormOwnProps = {
  hasError?: boolean;
};

const mapStateToProps = (
  state: AppState,
  { hasError }: LoginAuthFormOwnProps
) => ({
  applications: getAvailableApplications(state),
  hasError,
  isDarkMode: isDarkTheme(state),
});

const loginAuthFormConnector = connect(mapStateToProps, {
  onSubmit: session.signIn,
});

export const LoginFormContainer = loginAuthFormConnector(LoginForm);
export type LoginFormContainerProps = ConnectedProps<
  typeof loginAuthFormConnector
>;

type dsAuthFormOwnProps = {
  dsAgent?: DSAgents;
  hasError?: boolean;
  submitLabel?: string;
  withApplications?: boolean;
  withTitle?: boolean;
};

const signatureAuthFormMapStateToProps = (
  state: AppState,
  {
    dsAgent,
    hasError,
    submitLabel,
    withApplications,
    withTitle,
  }: dsAuthFormOwnProps
) => ({
  applications: getAvailableApplications(state) as
    AvailableApplication[] | undefined,
  hasError,
  dsAgent,
  dsKeyTypes: getAvailableDSKeyTypes(state),
  allowedDSMediaTypes: getAllowedDSMediaTypes(state),
  forbiddenDSMediaTypes: getForbiddenDSMediaTypes(state),
  signAlgo: getSignAlgo(state),
  signatureType: 'attached' as SignatureType,
  signData: [getConnectionId(state) ?? ''],
  submitLabel,
  withApplications,
  withTitle,
});

const signatureAuthFormMapDispatchToProps = {
  signInFailure: session.signInFailure,
  signIn: session.signIn,
};

const signatureAuthFormMergeProps = (
  msp: ReturnType<typeof signatureAuthFormMapStateToProps>,
  { signInFailure, signIn }: typeof signatureAuthFormMapDispatchToProps
) => ({
  ...msp,
  onError: (e: Error) => {
    signInFailure(e);
  },
  onSubmit: (args: SignActionArgs, appName?: AvailableApplication['name']) => {
    signIn({
      type: AuthMethod.DIGITAL_SIGNATURE,
      signature: args.signatures[0],
      application: appName ?? null,
      message: '',
    });
  },
});

const signatureAuthFormConnector = connect(
  signatureAuthFormMapStateToProps,
  signatureAuthFormMapDispatchToProps,
  signatureAuthFormMergeProps
);

export const DSLoginFormContainer = signatureAuthFormConnector(DSAgentForm);

export type DSLoginFormContainerProps = ConnectedProps<
  typeof signatureAuthFormConnector
>;

const windowsADAuthFormConnector = connect(
  (state: AppState, { hasError }: { hasError?: boolean }) => ({
    applications: getAvailableApplications(state),
    hasError: hasError ?? false,
  }),
  { onSubmit: session.signIn }
);

export const WindowsADFormContainer = windowsADAuthFormConnector(
  WindowsADAuthContainer
);

export type WindowsADFormContainerProps = ConnectedProps<
  typeof windowsADAuthFormConnector
>;

const kerberosAuthFormConnector = connect(
  (state: AppState, { hasError }: { hasError?: boolean }) => ({
    applications: getUserApplications(state),
    hasError: hasError ?? false,
    kerberosMode: true as const,
  }),
  { onSubmit: session.signIn }
);

export const KerberosFormContainer = kerberosAuthFormConnector(
  WindowsADAuthContainer
);

export type KerberosFormContainerProps = ConnectedProps<
  typeof kerberosAuthFormConnector
>;
